import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import { NotePriorityValues } from "@/types";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type {
  FacilityGovernancePerson,
  GovernanceTitle
} from "@/pages/facilities/[facilityId]/people/governance/types";
import {
  buildGovernanceTitleOptions,
  getGovernanceTitleFieldState,
  GOVERNANCE_ROLE_UPDATED_NOTE_TITLE
} from "@/pages/facilities/[facilityId]/people/governance/_utils";
import { UPDATE_GOVERNANCE_ROLE_MUTATION, type UpdateGovernanceRoleMutationData } from "./sectionGovernanceMutations";
import { GOVERNANCE_TITLES_QUERY, type SectionGovernanceTargetType } from "./sectionGovernanceQueries";

export interface EditSectionGovernanceRoleSubject {
  id: string;
  title: GovernanceTitle;
  person?: FacilityGovernancePerson;
}

interface EditSectionGovernanceRoleModalProps {
  open: boolean;
  role: EditSectionGovernanceRoleSubject | null;
  organizationName?: string;
  organizationSecondaryText?: string;
  targetType: SectionGovernanceTargetType;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

interface FormValues {
  governanceTitleId: string;
  internalNote: string;
}

interface SubmitEditSectionGovernanceRoleArgs {
  associationId: string;
  values: FormValues;
  updateGovernanceRole: (options: {
    variables: {
      input: {
        id: string;
        governanceTitleId: string;
        note?: { content: string; priority: string; title?: string } | null;
      };
    };
  }) => Promise<{ data?: UpdateGovernanceRoleMutationData | null }>;
}

export async function submitEditSectionGovernanceRole({
  associationId,
  values,
  updateGovernanceRole
}: SubmitEditSectionGovernanceRoleArgs): Promise<boolean> {
  const trimmedNote = values.internalNote.trim();

  const result = await updateGovernanceRole({
    variables: {
      input: {
        id: associationId,
        governanceTitleId: values.governanceTitleId,
        note: trimmedNote
          ? { content: trimmedNote, priority: NotePriorityValues.NONE, title: GOVERNANCE_ROLE_UPDATED_NOTE_TITLE }
          : null
      }
    }
  });

  if (!result.data?.updateGovernanceRole.success) {
    showErrorSnackbar(result.data?.updateGovernanceRole.message || "Unable to update governance role.");
    return false;
  }

  showSnackbar("Governance role updated");
  return true;
}

export function EditSectionGovernanceRoleModal({
  open,
  role,
  organizationName,
  organizationSecondaryText,
  targetType,
  onClose,
  refetchQueries = []
}: EditSectionGovernanceRoleModalProps) {
  const [updateGovernanceRole, { loading: saving }] = useMutation(UPDATE_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries
  });
  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(GOVERNANCE_TITLES_QUERY, {
    variables: { targetType },
    fetchPolicy: "cache-first",
    skip: !open || !role
  });
  const governanceTitleOptions = buildGovernanceTitleOptions(titlesData?.governanceTitles);
  const titleFieldState = getGovernanceTitleFieldState({
    loading: titlesLoading,
    hasError: Boolean(titlesError),
    optionCount: governanceTitleOptions.length
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    values: {
      governanceTitleId: role?.title.id ?? "",
      internalNote: ""
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (!role) {
      return;
    }

    try {
      const success = await submitEditSectionGovernanceRole({
        associationId: role.id,
        values,
        updateGovernanceRole
      });
      if (success) {
        handleClose();
      }
    } catch {
      showErrorSnackbar("Unable to update governance role.");
    }
  };

  if (!role) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Edit Governance Role"
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={saving}>
            Save
          </Button>
        </>
      }
    >
      <div key={role.id} className="flex flex-col gap-6">
        {role.person ? (
          <ListItem
            useAvatar
            compact
            picture={role.person.profilePhoto}
            iconName="person"
            overline={role.person.pgaId || undefined}
            headline={role.person.displayName}
            supportingText={role.person.latestProgram?.programType?.name || undefined}
          />
        ) : organizationName ? (
          <ListItem
            compact
            iconName="flag"
            iconBackground
            headline={organizationName}
            supportingText={organizationSecondaryText}
          />
        ) : null}

        <Controller
          name="governanceTitleId"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <Select
              label="Role"
              options={governanceTitleOptions}
              value={value}
              onSelect={onChange}
              disabled={titleFieldState.disabled}
              error={fieldState.invalid || Boolean(titleFieldState.errorText)}
              errorText={titleFieldState.errorText ?? fieldState.error?.message}
              supportingText={titleFieldState.supportingText}
              required
            />
          )}
        />

        <Controller
          name="internalNote"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Internal Note (Optional)"
              value={value}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
